import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { post } from "../../lib/api";
import { EXPRESS_HOLDER_ID } from "../../lib/holders";
import {
  IDENTITY_EXTERNAL_REF_MAX,
  IDENTITY_LABEL_MAX,
  ID_TAG_MAX,
  expiryDateError,
  expiryDateNotice,
  idTagError,
  identityLabelError,
  normaliseIdTag,
  vinError,
} from "../../lib/identity";
import type { Identity, TokenType } from "../../lib/types";
import { HOLDERS_KEY, HolderSelect } from "../holders/HolderSelect";
import { Button } from "../ui/Button";
import { DateField } from "../ui/DateField";
import { ErrorNotice, Notice } from "../ui/Feedback";
import { Field, SelectField } from "../ui/Input";
import { MicroLabel } from "../ui/Text";

/**
 * Registering a card before anyone taps it.
 *
 * The tag is typed from a card face, and a transposed character produces a
 * registration that looks fine and authorises nothing. Hence the tag rules in
 * `lib/identity.ts` and the deliberate absence of anything resembling bulk entry.
 *
 * ## Why it exists at all
 *
 * It is the only way a card gets in: a station refuses a card it has never been
 * told about.
 */

export function RegisterIdentity({ onClose }: { readonly onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [idTag, setIdTag] = useState("");
  const [label, setLabel] = useState("");
  const [externalRef, setExternalRef] = useState("");
  const [tokenType, setTokenType] = useState<TokenType>("rfid");
  // Express is what the server assumes too; preselected so the common case is
  // one fewer decision, and visible so it is never a silent default.
  const [holderId, setHolderId] = useState(EXPRESS_HOLDER_ID);
  const [expiryDate, setExpiryDate] = useState("");
  const [registered, setRegistered] = useState<Identity | undefined>(undefined);

  const register = useMutation({
    mutationFn: (body: unknown) => post<Identity>("/identities", body),
    onSuccess: (identity) => {
      setRegistered(identity);
      setIdTag("");
      setLabel("");
      setExpiryDate("");
      // The list and its filtered pages all change.
      void queryClient.invalidateQueries({ queryKey: ["identities"] });
      // The holder's card count.
      void queryClient.invalidateQueries({ queryKey: HOLDERS_KEY });
    },
  });

  const tagIssue = tokenType === "vin" ? vinError(idTag) : idTagError(idTag);
  const labelIssue = identityLabelError(label);
  const expiryIssue = expiryDateError(expiryDate);
  const expiryWarning = expiryDateNotice(expiryDate);

  const canSubmit =
    idTag.trim() !== "" &&
    label.trim() !== "" &&
    holderId !== "" &&
    tagIssue === undefined &&
    labelIssue === undefined &&
    expiryIssue === undefined &&
    !register.isPending;

  return (
    // Named so the panel is a landmark an operator can jump to, and so a test can
    // scope to it rather than to the page — the same reason `RegisterStation` does.
    <section
      aria-label="Register a card"
      className="flex flex-col gap-4 border border-rule bg-paper px-4 py-4"
    >
      <div className="flex items-baseline justify-between gap-4">
        <MicroLabel>Register a card</MicroLabel>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>

      {register.isError ? <ErrorNotice error={register.error} /> : null}

      {registered === undefined ? null : (
        <Notice tone="ok">
          <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span>
              {registered.label} registered as {registered.idTag}, held by {registered.holder.name}.{" "}
              {registered.allowance === null
                ? "It authorises now, with no quota until its holder has one."
                : `It draws on ${registered.allowance.holder.name}'s ${registered.allowance.policy.name} pool.`}
            </span>
            {/* Spec 6.1 — no policy means tracked but never flagged. That is a
                real state, so this offers the next step rather than implying the
                card is unfinished. */}
            <Button
              variant="ghost"
              onClick={() => navigate(`/identities?identity=${encodeURIComponent(registered.id)}`)}
            >
              Open the card
            </Button>
          </span>
        </Notice>
      )}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!canSubmit) return;
          setRegistered(undefined);

          register.mutate({
            idTag: normaliseIdTag(idTag, tokenType),
            tokenType,
            label: label.trim(),
            ...(externalRef.trim() === "" ? {} : { externalRef: externalRef.trim() }),
            holderId,
            // The picker yields a bare calendar date. The server wants a datetime,
            // and midnight UTC is the reading that does not silently move the
            // expiry a day either way for a reader in another zone.
            ...(expiryDate === ""
              ? {}
              : { expiryDate: new Date(`${expiryDate}T00:00:00.000Z`).toISOString() }),
          });
        }}
        className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2 xl:grid-cols-3"
      >
        <SelectField
          label="Token"
          value={tokenType}
          onChange={(event) => setTokenType(event.target.value as TokenType)}
          required
          hint={
            tokenType === "vin"
              ? "The vehicle authenticates itself with its VIN (Plug & Charge style)."
              : "A card tapped on the station's reader."
          }
        >
          <option value="rfid">RFID card</option>
          <option value="vin">Vehicle VIN</option>
        </SelectField>

        <Field
          label={tokenType === "vin" ? "VIN" : "ID tag"}
          value={idTag}
          onChange={(event) => setIdTag(event.target.value)}
          placeholder={tokenType === "vin" ? "1HGCM82633A004352" : "04A1B2C3D4"}
          maxLength={ID_TAG_MAX}
          spellCheck={false}
          autoComplete="off"
          required
          autoFocus
          hint={
            tokenType === "vin"
              ? "17 characters, stored in capitals."
              : "Exactly what the card presents. Read it off the card, or off a station's Authorize frame."
          }
          {...(tagIssue === undefined ? {} : { error: tagIssue })}
        />

        <Field
          label="Label"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder="Van 12, or User's Name"
          maxLength={IDENTITY_LABEL_MAX}
          required
          hint="Personal data. Recorded in the audit trail as the name you gave this card."
          {...(labelIssue === undefined ? {} : { error: labelIssue })}
        />

        <Field
          label="External ref"
          value={externalRef}
          onChange={(event) => setExternalRef(event.target.value)}
          placeholder="2CM-3174"
          maxLength={IDENTITY_EXTERNAL_REF_MAX}
          hint="Optional. What a partner calls this card or vehicle — sent as the plate when sessions are pushed. Empty sends the label."
        />

        <HolderSelect
          label="Holder"
          value={holderId}
          onChange={setHolderId}
          required
          hint="The person or group this belongs to. It shares the allowance of the nearest group above it that has one."
        />

        <DateField
          label="Expires"
          value={expiryDate}
          onChange={setExpiryDate}
          // UTC, because that is the calendar this value is committed in: the
          // date is submitted as midnight UTC (see the mutation above), so the
          // day the grid marks as today has to be UTC's day too. Anything else
          // would mark one square and store another.
          zone="UTC"
          placeholder="Never expires"
          {...(expiryIssue === undefined
            ? expiryWarning === undefined
              ? { hint: "Optional. Most fleet cards do not expire." }
              : { hint: expiryWarning }
            : { error: expiryIssue })}
        />

        <div className="flex items-end">
          <Button
            type="submit"
            variant="primary"
            disabled={!canSubmit}
            pending={register.isPending}
          >
            Register card
          </Button>
        </div>
      </form>

      <p className="m-0 max-w-prose text-micro tracking-normal normal-case text-ink-faint">
        The ID tag cannot be changed afterwards — it is the evidence of what the charger presented,
        and editing it would rewrite the history of every session already attributed to this card.
      </p>
    </section>
  );
}
